import cn from 'classnames';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useApp } from '../helpers/api-helpers';
import Button from './Button';
import styles from './PurchaseCTA.module.css';

export const PurchaseCTAContext = React.createContext({
  course: null,
  product: null, // Product to offer if not already subscribed
  source: null, // Dialog or app feature to attribute the click to
  visible: false,
  show() {},
  hide() {},
  setup() {},
});

export function usePurchaseCTA() {
  return useContext(PurchaseCTAContext);
}

export function PurchaseCTAProvider({ children, ...initialData }) {
  const [data, setData] = useState(initialData);
  const [visible, setVisible] = useState(true);
  const context = useMemo(
    () => ({
      ...data,
      visible,
      show: () => {
        if (!visible) {
          setVisible(true);
        }
      },
      hide: () => {
        if (visible) {
          setVisible(false);
        }
      },
      setup: setData,
    }),
    [data, visible],
  );

  return (
    <PurchaseCTAContext.Provider value={context}>
      {children}
    </PurchaseCTAContext.Provider>
  );
}

export function PurchaseCTA({ className }) {
  const { openProductCheckout } = useApp();
  const { source, course, product, visible } = usePurchaseCTA();

  const onClick = useCallback(() => {
    gtag('event', 'ClickCTA', {
      event_category: source || 'UnknownSource',
      event_label: course?.name || 'UnknownCourse',
      source: source || 'UnknownSource',
      course: course?.name || 'UnknownCourse',
      product: product?.name || 'UnknownProduct',
    });
    openProductCheckout(product?.id, { course: course?.id });
  }, [product, course, source, openProductCheckout]);

  if (!product?.id || !visible) {
    return null;
  }

  return (
    <div className={cn(styles.wrapper, className)}>
      <span className={styles.label}>Ready for Full Access?</span>
      <Button className={styles.button} onClick={onClick}>
        Subscribe
      </Button>
    </div>
  );
}

// This allows non functional components to easily suppress the purchase cta
export function PurchaseCTASuppressor({ children }) {
  const purchaseCTA = usePurchaseCTA();
  useEffect(() => {
    purchaseCTA.hide();
    return () => purchaseCTA.show();
  }, [purchaseCTA]);

  return <PurchaseCTAProvider {...purchaseCTA}>{children}</PurchaseCTAProvider>;
}
